/**
 * /checkout — review order, confirm room number, pick tip, place + pay.
 *
 * Flow:
 *   1. POST /api/v1/orders with cart items + tip
 *   2. POST /api/v1/square/create-payment (mock sourceId in sandbox)
 *   3. Clear cart, navigate to /orders/:orderId tracking page
 *
 * If step 2 fails after step 1 succeeds, the order persists with
 * `paymentStatus: 'unpaid'`. The tracking page surfaces this so the
 * teacher can retry, and the admin dashboard can clean up.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FulfillmentMethod, Order } from '@cracked-mug/shared';
import { computeTax, formatOrderNumber, formatPrice } from '@cracked-mug/shared';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../lib/cn';
import { OrderSummary, type OrderSummaryLine } from '../components/orders/OrderSummary';
import { TipSelector } from '../components/checkout/TipSelector';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { useSettings } from '../hooks/useSettings';
import { useSquareCard } from '../hooks/useSquareCard';
import { selectCartSubtotalCents, useCartStore } from '../store/cartStore';
import { createOrder } from '../services/ordersApi';
import { createSquarePayment } from '../services/squareApi';
import { pushToast } from '../store/toastStore';

export function Checkout() {
  const items = useCartStore((s) => s.items);
  const subtotalCents = useCartStore(selectCartSubtotalCents);
  const clearCart = useCartStore((s) => s.clear);
  const { appUser } = useAuth();
  const { settings, isLoading: settingsLoading } = useSettings();
  const {
    enabled: squareEnabled,
    card,
    ready: cardReady,
    error: cardError,
  } = useSquareCard('card-container');
  const navigate = useNavigate();

  const [roomNumber, setRoomNumber] = useState(appUser?.roomNumber ?? '');
  const [fulfillmentMethod, setFulfillmentMethod] = useState<FulfillmentMethod>('delivery');
  const [tipCents, setTipCents] = useState(0);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // If createOrder succeeds but the payment step fails, we hold the placed
  // order here so retrying pays *that* order instead of creating a duplicate.
  const [placedOrder, setPlacedOrder] = useState<Order | null>(null);
  // Synchronous re-entry guard. `setSubmitting(true)` doesn't disable the
  // button until the next render, so a same-tick double-tap could otherwise
  // slip two createOrder calls through before `submitting` takes effect.
  const submitGuard = useRef(false);
  // Set once an order is fully placed + paid, so the empty-cart redirect below
  // (clearCart() empties the cart on success) can't override the nav to the
  // tracking page.
  const placedSuccessfully = useRef(false);

  const taxCents = useMemo(
    () => (settings ? computeTax(subtotalCents, settings.taxRate) : 0),
    [subtotalCents, settings],
  );
  const totalCents = subtotalCents + taxCents + tipCents;

  useEffect(() => {
    if (placedSuccessfully.current) return;
    if (items.length === 0 && !submitting) {
      navigate('/order', { replace: true });
    }
  }, [items.length, submitting, navigate]);

  useEffect(() => {
    if (appUser?.roomNumber && roomNumber === '') {
      setRoomNumber(appUser.roomNumber);
    }
  }, [appUser, roomNumber]);

  const summaryLines: OrderSummaryLine[] = items.map((line) => ({
    key: line.cartItemId,
    name: line.itemName,
    quantity: line.quantity,
    basePriceCents: line.basePriceCents,
    selectedCustomizations: line.selectedCustomizations,
    specialInstructions: line.specialInstructions,
  }));

  const handlePlaceOrder = async (): Promise<void> => {
    if (submitGuard.current) return;
    if (roomNumber.trim().length === 0) {
      setError('Please enter your classroom or room number.');
      return;
    }
    submitGuard.current = true;
    setError(null);
    setSubmitting(true);
    try {
      // Collect a real card token first (when Square is configured) so a bad
      // card never creates an unpaid order. Falls back to the mock token until
      // Square keys are set.
      let sourceId = 'cnon:mock';
      if (squareEnabled) {
        if (!card) {
          setError('The payment form is still loading — try again in a moment.');
          return;
        }
        const tokenResult = await card.tokenize();
        if (tokenResult.status !== 'OK' || !tokenResult.token) {
          setError('Please check your card details and try again.');
          return;
        }
        sourceId = tokenResult.token;
      }

      // Reuse the already-placed order if a prior attempt got past creation but
      // failed at payment — otherwise we'd file a second order for the same cart.
      let order = placedOrder;
      if (!order) {
        order = await createOrder({
          items: items.map((line) => ({
            itemId: line.itemId,
            quantity: line.quantity,
            selectedCustomizations: line.selectedCustomizations.map((c) => ({
              groupId: c.groupId,
              optionIds: c.optionIds,
            })),
            specialInstructions: line.specialInstructions,
          })),
          tipCents,
          teacherRoom: roomNumber.trim(),
          fulfillmentMethod,
          specialInstructions: specialInstructions.trim(),
        });
        setPlacedOrder(order);
      }

      // Pay — real card token when Square is live, mock token otherwise.
      await createSquarePayment({ orderId: order.orderId, sourceId });

      placedSuccessfully.current = true;
      setPlacedOrder(null);
      clearCart();
      pushToast({
        variant: 'success',
        title: 'Order placed!',
        description: `We'll let you know when it's ready.`,
      });
      navigate(`/orders/${order.orderId}`, { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to place order';
      setError(msg);
      pushToast({ variant: 'error', title: 'Couldn’t place your order', description: msg });
    } finally {
      submitGuard.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5 pb-32">
      <Link
        to="/order"
        className="inline-flex items-center gap-1 text-sm text-wca-gray-mid hover:text-wca-black"
      >
        <ArrowLeft size={16} />
        Back to menu
      </Link>

      <h1 className="font-display text-3xl">Checkout</h1>

      <OrderSummary
        items={summaryLines}
        subtotalCents={subtotalCents}
        taxCents={taxCents}
        tipCents={tipCents}
        totalCents={totalCents}
      />

      <section className="bg-wca-surface rounded-card shadow-card p-5 space-y-3">
        <p className="block font-medium text-sm">How would you like it?</p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setFulfillmentMethod('delivery')}
            aria-pressed={fulfillmentMethod === 'delivery'}
            className={cn(
              'rounded-lg border-2 px-3 py-3 text-sm font-medium transition-colors',
              fulfillmentMethod === 'delivery'
                ? 'bg-wca-red text-white border-wca-red'
                : 'bg-wca-surface text-wca-black border-wca-gray-light hover:border-wca-red',
            )}
          >
            <span className="block text-base">Delivery</span>
            <span className="block text-xs opacity-80 mt-0.5">A barista brings it to you</span>
          </button>
          <button
            type="button"
            onClick={() => setFulfillmentMethod('pickup')}
            aria-pressed={fulfillmentMethod === 'pickup'}
            className={cn(
              'rounded-lg border-2 px-3 py-3 text-sm font-medium transition-colors',
              fulfillmentMethod === 'pickup'
                ? 'bg-wca-red text-white border-wca-red'
                : 'bg-wca-surface text-wca-black border-wca-gray-light hover:border-wca-red',
            )}
          >
            <span className="block text-base">Pickup</span>
            <span className="block text-xs opacity-80 mt-0.5">Grab it at the counter</span>
          </button>
        </div>
        <label htmlFor="room-number" className="block font-medium text-sm pt-2">
          Room number
        </label>
        <input
          id="room-number"
          type="text"
          value={roomNumber}
          onChange={(e) => setRoomNumber(e.target.value)}
          placeholder="e.g. 214"
          maxLength={20}
          className="w-full rounded-lg bg-wca-gray-light p-3 focus:outline-none focus:ring-2 focus:ring-wca-red text-sm"
          autoComplete="off"
          inputMode="numeric"
        />
        <p className="text-xs text-wca-gray-mid">
          {fulfillmentMethod === 'delivery'
            ? "We'll deliver here."
            : 'For our records — still required even on pickup.'}
        </p>
      </section>

      <section className="bg-wca-surface rounded-card shadow-card p-5 space-y-3">
        <label htmlFor="order-instructions" className="block font-medium text-sm">
          Order notes (optional)
        </label>
        <textarea
          id="order-instructions"
          value={specialInstructions}
          onChange={(e) => setSpecialInstructions(e.target.value)}
          rows={2}
          maxLength={280}
          placeholder="Anything the barista should know about this whole order?"
          className="w-full rounded-lg bg-wca-gray-light p-3 focus:outline-none focus:ring-2 focus:ring-wca-red resize-none text-sm"
        />
      </section>

      <section className="bg-wca-surface rounded-card shadow-card p-5">
        <TipSelector subtotalCents={subtotalCents} tipCents={tipCents} onChange={setTipCents} />
      </section>

      <section className="bg-wca-surface rounded-card shadow-card p-5 space-y-3">
        <p className="font-medium text-sm">Payment</p>
        {squareEnabled ? (
          <>
            <div id="card-container" className="min-h-[44px]" />
            {cardError !== null && <p className="text-xs text-wca-red">{cardError}</p>}
            <p className="text-xs text-wca-gray-mid">Your card is charged securely by Square.</p>
          </>
        ) : (
          <p className="text-xs text-wca-gray-mid">
            Card payments aren&rsquo;t switched on yet — your order is placed without a charge for
            now.
          </p>
        )}
      </section>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3" role="alert">
          {error}
        </p>
      )}

      {placedOrder !== null && (
        <p className="text-sm text-wca-gray-mid bg-wca-gray-light rounded-lg p-3" role="status">
          Your order <span className="font-mono">{formatOrderNumber(placedOrder.orderNumber)}</span>{' '}
          was placed, but the payment didn’t go through. Tap “Retry payment” — you won’t be charged
          twice or file a second order.
        </p>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-wca-surface border-t border-wca-gray-light px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs text-wca-gray-mid">Total</p>
          <p className="font-mono text-xl text-wca-red tabular-nums">{formatPrice(totalCents)}</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          disabled={
            submitting || settingsLoading || items.length === 0 || (squareEnabled && !cardReady)
          }
          onClick={() => void handlePlaceOrder()}
        >
          {submitting
            ? placedOrder !== null
              ? 'Retrying payment…'
              : 'Placing order…'
            : placedOrder !== null
              ? 'Retry payment'
              : 'Place order'}
        </Button>
      </div>
    </div>
  );
}
